import { useContext, useEffect } from 'react';

import { Trans } from '@lingui/react/macro';
import { usePrevious } from '@react-pdf-viewer/core';
import { Checkbox } from '@repo/videa-components/src/index';
import { Label } from '@repo/videa-components/src/ui/label';
import classNames from 'classnames';
import { useAtom } from 'jotai';
import { useParams } from 'react-router-dom';

import { ExternalPatientContext } from '@/app/external/constants';
import { type AdultTreatmentKey } from '@/modules/annotator/patientEducationDialog/AdultTabs';
import type { PediatricTreatmentKey } from '@/modules/annotator/patientEducationDialog/PediatricTabs';
import { usePediatricTreatmentOptions } from '@/modules/annotator/patientEducationDialog/PediatricTabs';
import { mixpanelEvents, useTrackEvent } from '@/utils/mixpanel';

import { patientDownloadableSelectedTreatmentsAtom } from '../../atoms';
import { useGenerateQRCode } from '../hooks/useGenerateQRCode';
import { usePatientPrintoutData } from '../hooks/usePatientPrintoutData';
import { useAdultTreatmentOptions } from '../TreatmentOptions';

import { ThemeModeContext } from './QRPrintDownloadContent';

interface PreviewSidebarPatientEducationProps {
  isEditMode?: boolean;
}

export const PreviewSidebarPatientEducation = ({
  isEditMode,
}: PreviewSidebarPatientEducationProps) => {
  const mode = useContext(ThemeModeContext);
  const adultOptions = useAdultTreatmentOptions({
    getDefault: false,
    forPrintout: true,
  });
  const pediatricOptions = usePediatricTreatmentOptions();
  const options = [
    ...Object.values(adultOptions),
    ...Object.values(pediatricOptions),
  ];

  const { generateQRCode } = useGenerateQRCode();
  const { trackEvent } = useTrackEvent();
  const [selectedTreatments, setSelectedTreatments] = useAtom(
    patientDownloadableSelectedTreatmentsAtom,
  );
  const previousSelectedTreatmentsLength = usePrevious(
    selectedTreatments.length,
  );
  const { mixPanelData } = usePatientPrintoutData();
  const {
    isIframe,
    externalPatientId,
    externalImageId,
    patientId,
    trialStatus,
    centralizedBilling: isCentralizedBilling,
  } = useContext(ExternalPatientContext);
  const { id: selectedPatientId } = useParams<{ id: string }>();

  const toggleTreatment = (
    treatment: AdultTreatmentKey | PediatricTreatmentKey,
  ) => {
    setSelectedTreatments((prev) => {
      if (prev.includes(treatment)) {
        return prev.filter((t) => t !== treatment);
      }
      return [...prev, treatment];
    });
  };

  useEffect(() => {
    if (
      !isEditMode &&
      selectedTreatments.length !== previousSelectedTreatmentsLength
    ) {
      // Re-generate QR code when user selects/deselects a treatment
      generateQRCode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTreatments.length]);

  return (
    <div
      className={classNames('vai-font-inter', {
        'vai-text-foreground-dark': mode === 'dark',
        'vai-text-zinc-900': mode === 'light',
      })}
    >
      <div
        className={classNames(
          'vai-my-2',
          'vai-px-4 vai-py-2',
          'vai-flex vai-items-center vai-justify-left',
          'vai-rounded-sm',
          {
            'vai-bg-zinc-800': mode === 'dark',
            'vai-bg-zinc-100': mode === 'light',
          },
        )}
      >
        <span
          className={classNames(
            'vai-text-sm vai-font-medium',
            'vai-block',
            'vai-text-center vai-items-center',
            'vai-font-inter',
          )}
        >
          <Trans>Treatments</Trans>
        </span>
      </div>
      <div
        className={classNames(
          'vai-px-2 vai-mt-2',
          'vai-flex vai-flex-col vai-gap-2',
        )}
      >
        {options.map(({ value, label, isPediatric }) => (
          <div
            key={value}
            className={classNames(
              'vai-flex vai-items-center vai-gap-2 vai-cursor-pointer',
              'vai-py-2',
              'vai-border-b',
              {
                'vai-border-b-sidebar-border': mode === 'dark',
                'vai-border-b-zinc-200': mode === 'light',
              },
            )}
          >
            <Checkbox
              checked={selectedTreatments.includes(value)}
              id={`treatment-checkbox-${value}`}
              onCheckedChange={() => {
                toggleTreatment(value);

                trackEvent(mixpanelEvents.previewDialogPatientEducationToggle, {
                  source: 'visit_sidebar',
                  treatment: value,
                  selected: !selectedTreatments.includes(value),
                  ...mixPanelData,
                  isIframe: !!isIframe,
                  externalPatientId,
                  externalImageId,
                  patientId: selectedPatientId ?? patientId,
                  trialStatus,
                  centralizedBilling: isCentralizedBilling,
                });
              }}
            />
            <Label
              htmlFor={`treatment-checkbox-${value}`}
              className={classNames(
                'vai-leading-5 vai-text-sm vai-font-medium vai-font-inter vai-cursor-pointer',
                {
                  'vai-text-foreground-dark': mode === 'dark',
                  'vai-text-zinc-900': mode === 'light',
                },
              )}
            >
              {isPediatric && <Trans>Pediatric: </Trans>}
              {label}
            </Label>
          </div>
        ))}
      </div>
    </div>
  );
};
